// GitHub through the changelist bridge (/api/gh/rest/…, /api/gh/graphql).
// Every request runs with the signed-in user's own token on the server.
import { cachedGet, httpError, HttpError, LruMap, MemoryStore, parseLink, rateLimitResetAt, type CacheStore } from './cache';
import { budgetOf, classifyLimit, graphqlLimit, RateLimitError, serverOffset, type Budget, type RateLimit } from './rateLimit';
import { api, apiResponse, transport } from './http';
import { failureOf, retriesFor } from './failure';
import { GraphQLFailure, isSamlError, type GraphQLError } from './graphqlError';
import { orThrow, Scheduler, type Priority, type Resource } from './scheduler';
import { idbGet, idbSet } from './db';
import { identityGeneration, onIdentityChange, requireGeneration } from './identity';

const REST = '/api/gh/rest/';
const memory = new MemoryStore(300);

/** Responses too large to keep in memory (recursive trees); IndexedDB only. */
const noMemory = (key: string) => key.includes('/git/trees/');

const storeFor = (generation: number): CacheStore => ({
  async get(key) {
    requireGeneration(generation);
    return (await memory.get(key)) ?? (await idbGet('http', key));
  },
  async set(key, value) {
    requireGeneration(generation);
    if (!noMemory(key)) await memory.set(key, value);
    requireGeneration(generation);
    await idbSet('http', key, value);
  },
});

// ---------------------------------------------------------------- scheduling

/**
 * The one scheduler every GitHub call (and the changelist server's batch
 * endpoints) goes through: concurrency, priority, dedupe, the hourly
 * budgets, rate-limit pauses and retries (see scheduler.ts). Six requests
 * in flight keep a page well inside the changelist server's per-user rate
 * (20 a second, bursts of 60) and GitHub's advice against bursts; two more
 * are kept for what the user is waiting on.
 */
export const github = new Scheduler({
  concurrency: 6,
  interactiveReserve: 2,
  ageMs: 4_000,
  retries: (e) => retriesFor(failureOf(e)),
});

/**
 * How long a write the user made waits through a rate-limit pause before
 * it is refused: it is sent when the pause ends, and `onHeld` says when.
 */
export const WRITE_HOLD_MS = 2 * 60_000;

/** How a call is scheduled (see `Request`). */
export interface CallOptions {
  /** Default `visible`. */
  priority?: Priority;
  /** Estimated cost: GraphQL points (default 1), or 1 per REST request. */
  cost?: number;
  signal?: AbortSignal;
}

export { RateLimitError };

/** Epoch ms until which GitHub calls are paused, or null when not limited. */
export function rateLimitedUntil(): number | null {
  return github.limitedUntil();
}

/** The limit in force, or null. */
/**
 * What is left of the hour's REST (`core`) budget: what GitHub's latest
 * answer reported (successful reads report it, 304s included), less what
 * was sent since; null before any answer said, or once its hour is over.
 */
export function coreBudgetLeft(): number | null {
  return github.left('core');
}

export function rateLimitState(): RateLimit | null {
  return github.limitState();
}

/** For tests. */
export function resetRateLimit() {
  github.reset();
}

/** The resource a bridge call is counted against. */
function resourceOf(path: string): Resource {
  if (path.startsWith('/api/gh/graphql')) return 'graphql';
  return /^\/api\/gh\/rest\/search\//.test(path) ? 'search' : 'core';
}

/**
 * The error an answer means: GitHub's primary and secondary limits, and the
 * changelist server's own (429 `rate_limited`), become a RateLimitError (see
 * `classifyLimit`), which pauses calls. An answer to a call made under
 * another signed-in account notes nothing.
 */
function noteHttpError(e: unknown, generation: number, resource: Resource): unknown {
  if (!(e instanceof HttpError) || generation !== identityGeneration()) return e;
  github.noteBudget(e.budget);
  const l = classifyLimit({
    status: e.status,
    body: e.body,
    retryAfter: e.retryAfter,
    resetAt: e.resetAt,
    budget: e.budget,
    known: github.budget(resource),
  });
  return l ? new RateLimitError(l) : e;
}

/** Schedule a call and answer as the throwing API does. */
async function call<T>(
  opts: CallOptions,
  req: { resource: Resource; key?: string; idempotent?: boolean; cost?: number; holdUpTo?: number },
  send: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  return orThrow(
    await github.run({
      ...req,
      priority: opts.priority ?? 'visible',
      cost: opts.cost ?? req.cost,
      signal: opts.signal,
      send,
    }),
  );
}

/**
 * Note a rate limit an answer from the changelist server reports (its own
 * 429 `rate_limited`, or a GitHub limit it met making calls for the user,
 * as when publishing), pausing GitHub calls as any limit does. Returns
 * `e` as it was, for the caller to fail with.
 */
export function noteServerLimit(e: unknown): unknown {
  if (typeof e !== 'object' || e === null || noted.has(e)) return e;
  const limit = noteHttpError(e, identityGeneration(), 'changelist');
  if (limit instanceof RateLimitError) noted.set(e, github.noteLimit(limit.limit, 'changelist'));
  return e;
}

/** Answers whose limit `noteServerLimit` already counted, with the pause it kept: never counted twice. */
const noted = new WeakMap<object, RateLimit>();

/**
 * A read from the changelist server's own endpoints, scheduled with the
 * GitHub calls it is made beside (both count against the server's per-user
 * rate): its 429 pauses calls like any rate limit. Requests with the same
 * `key` in flight share one send.
 */
export function serverRead<T>(send: (signal: AbortSignal) => Promise<T>, opts: CallOptions & { key?: string } = {}): Promise<T> {
  const generation = identityGeneration();
  return call(opts, { resource: 'changelist', key: opts.key, cost: 0 }, async (signal) => {
    try {
      return await send(signal);
    } catch (e) {
      // `send` may go through `noteServerLimit` (server.ts's `api`): its limit is counted already.
      const kept = typeof e === 'object' && e !== null ? noted.get(e) : undefined;
      throw kept ? new RateLimitError(kept, true) : noteHttpError(e, generation, 'changelist');
    }
  });
}

function restUrl(path: string): string {
  return REST + path.replace(/^\/+/, '');
}

/** One conditional GET through the scheduler. */
/** A REST read's own options: the media type, and `uncached` for a body the caller keeps itself (nothing is revalidated or stored in the response cache). */
export type ReadOptions = CallOptions & { accept?: string; uncached?: boolean };

/** The response cache of an `uncached` read: holds nothing. */
const NO_STORE: CacheStore = { get: async () => undefined, set: async () => {} };

function cachedRead<T>(url: string, opts: ReadOptions): Promise<{ body: T; link: string | null }> {
  const generation = identityGeneration();
  const resource = resourceOf(url);
  return call(opts, { resource, key: `GET ${opts.accept ?? ''} ${url}` }, async (signal) => {
    try {
      const response = await cachedGet<T>(url, { accept: opts.accept, transport, store: opts.uncached ? NO_STORE : storeFor(generation), signal });
      requireGeneration(generation);
      // A successful answer reports the hour's budget as an error's does: what the scheduler and coreBudgetLeft read.
      github.noteBudget(response.budget);
      return { body: response.body, link: response.link };
    } catch (e) {
      throw noteHttpError(e, generation, resource);
    }
  });
}

/** GET a REST resource with ETag revalidation (none with `uncached`). */
export async function rest<T>(path: string, opts: ReadOptions = {}): Promise<T> {
  return (await cachedRead<T>(restUrl(path), opts)).body;
}

/** What a paged REST read takes: at most `maxPages` pages; `onPage` hears how many items are read after each, and the items so far. */
export type PagedOptions<T = unknown> = CallOptions & { uncached?: boolean; maxPages?: number; onPage?: (items: number, sofar: readonly T[]) => void };

/** GET every page of a REST list (follows `Link: rel="next"`), at most `maxPages`. */
export function restAll<T>(path: string, opts: PagedOptions<T> = {}): Promise<T[]> {
  return restPages<T[], T>(path, (body) => body, opts);
}

/**
 * GET every page of a REST list whose pages wrap their items in an object
 * (e.g. `{ total_count, repositories: [...] }`); `items` picks them out.
 */
export async function restPages<B, T>(
  path: string,
  items: (body: B) => T[],
  opts: PagedOptions<T> = {},
): Promise<T[]> {
  const out: T[] = [];
  let url: string | null = restUrl(path);
  for (let i = 0; url && i < (opts.maxPages ?? 30); i++) {
    const page: { body: B; link: string | null } = await cachedRead<B>(url, opts);
    out.push(...(items(page.body) ?? []));
    opts.onPage?.(out.length, out);
    const next = parseLink(page.link).next;
    url = next ? toBridge(next) : null;
  }
  return out;
}

/** Map an absolute api.github.com URL from a Link header back onto the bridge. */
export function toBridge(url: string): string {
  if (url.startsWith(REST)) return url;
  try {
    const u = new URL(url);
    return REST + (u.pathname.replace(/^\/(api\/v3\/)?/, '') + u.search);
  } catch {
    return url;
  }
}

/**
 * A REST write: interactive by default, never retried or merged, and held
 * through a pause that ends within WRITE_HOLD_MS.
 */
export async function restWrite<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts: CallOptions = {},
): Promise<T> {
  const generation = identityGeneration();
  const url = restUrl(path);
  const resource = resourceOf(url);
  return call({ priority: 'interactive', ...opts }, { resource, idempotent: false, holdUpTo: WRITE_HOLD_MS }, async (signal) => {
    try {
      return await api<T>(url, { method, body, headers: { Accept: 'application/vnd.github+json' }, signal });
    } catch (e) {
      throw noteHttpError(e, generation, resource);
    }
  });
}

/**
 * The raw bytes of a REST resource (`application/vnd.github.raw+json`):
 * a blob, or a file at a ref. Throws an HttpError for any status but 200.
 */
export function restBytes(path: string, opts: CallOptions = {}): Promise<Uint8Array> {
  const generation = identityGeneration();
  const url = restUrl(path);
  const resource = resourceOf(url);
  return call(opts, { resource, key: `RAW ${url}` }, async (signal) => {
    const res = await transport(url, { method: 'GET', headers: { Accept: 'application/vnd.github.raw+json' }, signal });
    requireGeneration(generation);
    github.noteBudget(budgetOf(res.headers));
    if (!res.ok) throw noteHttpError(httpError(res, await res.text().catch(() => '')), generation, resource);
    return new Uint8Array(await res.arrayBuffer());
  });
}

export type { GraphQLError };
export { GraphQLFailure, isSamlError };

export interface GraphQLResult<T> {
  data: T;
  /** Errors GitHub returned alongside data (e.g. FORBIDDEN for a SAML-protected repository). */
  errors: GraphQLError[];
}

/** GitHub's `rateLimit` object, when a document asks for it. */
interface RateLimitField {
  limit?: number;
  remaining?: number;
  used?: number;
  resetAt?: string;
}

/**
 * The graphql budget a document's own `rateLimit { limit remaining used resetAt }`
 * reported, its reset on this device's clock (`offset`: see `serverOffset`); or null.
 */
function budgetOfField(data: unknown, offset: number): Budget | null {
  const f = (data as { rateLimit?: RateLimitField } | null)?.rateLimit;
  if (!f || typeof f.limit !== 'number' || typeof f.remaining !== 'number' || !f.resetAt) return null;
  const resetAt = Date.parse(f.resetAt);
  if (Number.isNaN(resetAt)) return null;
  return { resource: 'graphql', limit: f.limit, remaining: f.remaining, used: f.used ?? f.limit - f.remaining, resetAt: resetAt - offset };
}

/** Send a GraphQL document once: data with any partial errors, or the error its answer means. */
async function sendGraphql<T>(
  query: string,
  variables: Record<string, unknown>,
  generation: number,
  signal: AbortSignal,
): Promise<GraphQLResult<T>> {
  let res: { data?: T | null; errors?: GraphQLError[] };
  let headers: Headers;
  try {
    ({ body: res, headers } = await apiResponse<{ data?: T | null; errors?: GraphQLError[] }>('/api/gh/graphql', {
      method: 'POST',
      body: { query, variables },
      signal,
    }));
  } catch (e) {
    requireGeneration(generation);
    throw noteHttpError(e, generation, 'graphql');
  }
  requireGeneration(generation);
  res ??= {};
  const budget = budgetOf(headers) ?? budgetOfField(res.data, serverOffset(headers));
  github.noteBudget(budget);
  const errors = res.errors ?? [];
  const limited = errors.find((e) => e.type === 'RATE_LIMITED');
  if (limited) throw new RateLimitError(graphqlLimit(budget, rateLimitResetAt(headers), limited.message, github.budget('graphql')));
  if (errors.length && !res.data) throw new GraphQLFailure(errors);
  return { data: res.data as T, errors };
}

/**
 * How a GraphQL document is scheduled; `notFoundOk` as for `graphql`.
 * `partial` (graphqlResult only): keep every error beside the data instead
 * of failing on one that nulled a top-level field, for a document whose
 * top-level fields are independent items (aliases, one per repository or
 * reference) that the caller marks hidden or failed one by one.
 */
export type GraphQLOptions = CallOptions & { notFoundOk?: boolean; partial?: boolean };

/**
 * Run a GraphQL document and return data with any partial errors. Throws
 * when there is no data, when an error nulled a top-level field (see
 * `fatalErrors`; not with `partial`), and on RATE_LIMITED (which pauses GitHub calls until the
 * limit is expected to reset). Queries are deduped and retried; mutations
 * are interactive by default, never are, and are held through a pause that
 * ends within WRITE_HOLD_MS.
 */
export async function graphqlResult<T>(
  query: string,
  variables: Record<string, unknown> = {},
  opts: GraphQLOptions = {},
): Promise<GraphQLResult<T>> {
  const generation = identityGeneration();
  const mutation = isMutation(query);
  const notFoundOk = opts.notFoundOk ?? !mutation;
  return call(
    { priority: mutation ? 'interactive' : 'visible', ...opts },
    mutation
      ? { resource: 'graphql', idempotent: false, holdUpTo: WRITE_HOLD_MS }
      : { resource: 'graphql', key: `POST graphql ${notFoundOk} ${!!opts.partial} ${query}\n${JSON.stringify(variables)}` },
    async (signal) => {
      const r = await sendGraphql<T>(query, variables, generation, signal);
      // Fatal errors fail the send, so a timeout is retried like any other transient failure.
      const fatal = opts.partial ? [] : fatalErrors(r.data, r.errors, notFoundOk);
      if (fatal.length) throw new GraphQLFailure(fatal, r.data);
      return r;
    },
  );
}

/**
 * Errors that cost the caller a whole top-level field (FORBIDDEN, SAML
 * enforcement, timeouts) whose path's root field came back null. Errors
 * deeper in the result (a branch protection rule the user may not read, one
 * inaccessible search hit) leave that one value null. NOT_FOUND is not fatal
 * when `notFoundOk` (queries: `repository: null` means "not found"); for a
 * mutation it means the input ID no longer exists, and the write failed.
 */
export function fatalErrors(data: unknown, errors: GraphQLError[], notFoundOk = true): GraphQLError[] {
  const root = (data ?? {}) as Record<string, unknown>;
  return errors.filter(
    (e) => !(notFoundOk && e.type === 'NOT_FOUND') && (!e.path?.length || root[String(e.path[0])] == null),
  );
}

/** True when the document's operation is a mutation. */
export function isMutation(query: string): boolean {
  return /^mutation\b/.test(query.replace(/^(\s|#[^\n]*)*/, ''));
}

/**
 * Run a GraphQL document. Throws a GraphQLFailure with GitHub's message when
 * an error nulled a top-level field, e.g. a repository hidden by SAML SSO
 * enforcement or a failed mutation. A query's NOT_FOUND is not an error (the
 * caller sees the null); a mutation's is, unless `notFoundOk` (a delete
 * whose target is already gone has done its job).
 */
export async function graphql<T>(query: string, variables: Record<string, unknown> = {}, opts: GraphQLOptions = {}): Promise<T> {
  const { data, errors } = await graphqlResult<T>(query, variables, opts);
  if (errors.length) console.warn('GraphQL partial errors', errors);
  return data;
}

export interface BlobText {
  text: string | null;
  binary: boolean;
}

/** Blobs already read this session, by repository and SHA. */
const blobMemo = new LruMap<string, BlobText>(500);
onIdentityChange(() => {
  memory.clear();
  blobMemo.clear();
  github.cancelAll(new Error('The signed-in account changed; reload this page.'));
});

/**
 * File content by blob SHA. Content-addressed, so cached forever (in memory
 * and IndexedDB); concurrent reads of one blob share one request, raised to
 * the most urgent caller's priority.
 */
export async function blob(owner: string, repo: string, sha: string, opts: CallOptions = {}): Promise<BlobText> {
  const key = `${owner.toLowerCase()}/${repo.toLowerCase()}/${sha}`;
  const hit = blobMemo.get(key);
  if (hit) return hit;
  const generation = identityGeneration();
  const kept = await idbGet('blobs', key);
  requireGeneration(generation);
  if (kept) {
    const b = { text: kept.text, binary: kept.binary };
    blobMemo.set(key, b);
    return b;
  }
  const bytes = await restBytes(`repos/${owner}/${repo}/git/blobs/${sha}`, opts);
  const binary = bytes.subarray(0, 8000).includes(0);
  const text = binary ? null : new TextDecoder('utf-8').decode(bytes);
  requireGeneration(generation);
  const b = { text, binary };
  blobMemo.set(key, b);
  await idbSet('blobs', key, { sha, text, binary, at: Date.now() });
  requireGeneration(generation);
  return b;
}

/** Whether `blob` would answer without a request: the blob is loaded in this tab or kept in this browser. */
export async function blobCached(owner: string, repo: string, sha: string): Promise<boolean> {
  const key = `${owner.toLowerCase()}/${repo.toLowerCase()}/${sha}`;
  if (blobMemo.get(key)) return true;
  return !!(await idbGet('blobs', key));
}

/** Raw file content at a ref (used when no blob SHA is known); null when it does not exist there. */
export async function contentAt(owner: string, repo: string, path: string, ref: string, opts: CallOptions = {}): Promise<string | null> {
  try {
    const body = await rest<unknown>(
      `repos/${owner}/${repo}/contents/${path.split('/').map(encodeURIComponent).join('/')}?ref=${encodeURIComponent(ref)}`,
      { ...opts, accept: 'application/vnd.github.raw+json' },
    );
    return typeof body === 'string' ? body : null;
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) return null;
    throw e;
  }
}
